/**
* @name: uploadFile.js
* @description: 文件上传方法
* @author: 于海威
* @create: 2020/07/27 11:10:24
* @update: 2020/07/27 11:10:24
*/

import fetch from './fetch';
import Vue from 'vue';

const UPLOAD_FILE_API = '/';

/**
* @function: getFileExtension 获取文件扩展名
* @param: { string } fileName 文件名称
* @return: { string } 文件扩展名
*/
function getFileExtension(fileName) {
  let pos = fileName.lastIndexOf('.');
  let extension = '';
  if (pos > -1) {
    extension = fileName.substring(pos + 1);
  }
  return extension;
}

/**
* @function: 函数名称 作用
* @param: { file } file 资源文件
* @param: { int } maxSize 文件大小，单位KB
* @return: { bool } 是否超过给定大小
*/
function checkFileSize(file, maxSize) {
  const { size } = file;
  if (size / 1024 > maxSize) {
    return false;
  }
  return true;
}

/**
* @function: dataURL2Blob base64转成二进制
* @param: { string } dataURL base64编码
* @return: { blob } 文件的二进制编码
*/
function dataURL2Blob(dataURL) {
  const arr = dataURL.split(',');
  const mime = arr[0].match(/:(.*?);/)[1];
  const blobStr = atob(arr[1]);
  let len = blobStr.length;
  const u8arr = new Uint8Array(len);
  while (len--) {
    u8arr[len] = blobStr.charCodeAt(len);
  }
  return new Blob([u8arr], { type: mime });
}

/**
* @function: pushFormData formData上传
* @param: { formData } formData 需要上传的formData
* @return: { Promise }
*/
function pushFormData(formData) {
  return fetch({
    api: UPLOAD_FILE_API,
    para: formData,
    loading: true,
    loadingText: '上传中...',
    type: 'formdata'
  });
}

/**
* @function: compressImg 图片压缩
* @param: { file } file 资源文件
* @param: { function } callback 回调函数
* @param: { object } { quality：最大1 float， maxWidth: 最大宽度 int，
*                    maxHeight： 最大高度 int，isBase64： 文件输出格式 bool} 压缩配置文件
* @return: { Promise }
*/
function compressImg(file, option = {
  quality: 0.8, maxWidth: 2000, maxHeight: 2000, isBase64: false
}) {
  const fileExtension = getFileExtension(file.name);
  let allowImgExtension = ['jpg', 'jpeg', 'png', 'gif'];
  if (allowImgExtension.indexOf(fileExtension.toLowerCase()) < 0) {
    return Vue.tips({
      content: '非图片格式文件',
      type: 'error'
    }).show().hide(2000);
  }
  return new Promise((resolve) => {
  // 正常图片获取宽高后做比例压缩
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = function (e) {
    // console.log(e);
      const img = new Image();
      img.src = e.target.result;
      img.onload = function () {
        const { width, height } = this;
        let scaleWidth; // 图片缩放宽度
        let scaleHeight; // 图片缩放高度
        if (width > height) {
          const scale = width / height;
          if (width > option.maxWidth) {
            scaleWidth = option.maxWidth;
            scaleHeight = scaleWidth / scale;
          } else {
            scaleWidth = width;
            scaleHeight = height;
          }
        } else {
          const scale = height / width;
          if (height > option.maxHeight) {
            scaleHeight = option.maxHeight;
            scaleWidth = scaleHeight / scale;
          } else {
            scaleHeight = height;
            scaleWidth = width;
          }
        }
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = scaleWidth;
        canvas.height = scaleHeight;
        ctx.drawImage(img, 0, 0, scaleWidth, scaleHeight);
        if (option.isBase64) {
          resolve(canvas.toDataURL(`image/${fileExtension}`, option.quality));
        }
        resolve(dataURL2Blob(canvas.toDataURL(`image/${fileExtension}`, option.quality)));
      };
    };
  });
}

/**
* @function: 函数名称 作用
* @param: { file } file 资源文件
* @param: { bool } isCheckSize 是否检查文件大小
* @param: { int } maxFileSize 最大文件大小,单位KB
* @param: { bool } isCompress 是否压缩，只有在文件是图片的情况下有效
* @return: { 返回值类型 } 返回值说明
*/
function uploadFile(file, isCheckSize = true, maxFileSize = 2048, isCompress = true) {
  if (isCheckSize) {
    if (checkFileSize(file, maxFileSize)) {
      if (isCompress) {
        return compressImg(file).then(newFile => {
          let formData = new FormData();
          formData.append('file', newFile);
          return pushFormData(formData);
});
      }
      let formData = new FormData();
      formData.append('file', file);
      return pushFormData(formData);
    }
      return Vue.tips({
        content: `文件大小超过${maxFileSize}KB`,
        type: 'error'
      }).show().hide(2000);
  }
  let formData = new FormData();
  formData.append('file', file);
  return pushFormData(formData);
}

export {
  getFileExtension, uploadFile, checkFileSize, compressImg
};
